// Pure helpers for the invoice form's Property & Building sections. Kept out of
// InvoiceFormModal.jsx so they can be unit-tested without dragging the modal's
// PDF previewer into Jest.
import { resolveRateType, flatAmountOf } from "./Api";

export const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const DEFAULT_GST_RATE = 5;

// Mirrors MAX_INVOICE_FILES on the server.
export const MAX_INVOICE_FILES = 10;

/**
 * The invoice files of a group: the keys every line carries. The server copies
 * the invoice files onto every line, so anything only some lines hold is that
 * line's own row file. Mirrors sharedAttachmentKeys on the server.
 */
export const sharedAttachmentKeys = (items = []) =>
  items
    .map((item) => new Set(item?.attachments || []))
    .reduce((acc, keys) => (acc === null ? Array.from(keys) : acc.filter((k) => keys.has(k))), null) || [];

/**
 * Every building the picker may offer, grouped by property. Accepts both the
 * `building: [{buildingName}]` shape (internal property list) and the
 * `buildings: ["A", "B"]` shape (vendor eligibility list).
 */
export const buildingChoicesByProperty = (properties = []) =>
  (properties || [])
    .map((p) => {
      const raw = Array.isArray(p.buildings) && p.buildings.length
        ? p.buildings.map((b) => (typeof b === "string" ? b : b?.buildingName))
        : (p.building || []).map((b) => b?.buildingName);
      return {
        propertyId: p._id,
        label: p.title || p.name || p._id,
        buildings: Array.from(new Set(raw.filter(Boolean))),
      };
    })
    .filter((p) => p.buildings.length)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

/**
 * Lay the buildings picked in the "Multiple buildings" checklist out as form
 * sections. The section the picker was opened from takes the first pick when it
 * has no building yet; every other pick becomes a new section appended at the
 * end. A building that already has a section is skipped.
 */
export const addBuildingSections = (groups, gi, picks, makeGroup) => {
  const has = new Set(groups.filter((g) => g.buildingName).map((g) => `${g.propertyId}::${g.buildingName}`));
  const fresh = picks.filter((p) => !has.has(`${p.propertyId}::${p.buildingName}`));
  if (!fresh.length) return groups;
  const next = [...groups];
  let rest = fresh;
  if (next[gi] && !next[gi].buildingName) {
    next[gi] = { ...next[gi], propertyId: fresh[0].propertyId, buildingName: fresh[0].buildingName };
    rest = fresh.slice(1);
  }
  return [...next, ...rest.map((p) => makeGroup(p.propertyId, p.buildingName))];
};

/**
 * Rebuild the form sections from an invoice's line items.
 *
 * An invoice is stored as one document per line, but it was ENTERED as property
 * sections each holding unit rows. Regrouping the lines by property + building
 * and laying their rows back out shows the invoice exactly the way it was
 * created. Both View and Edit use this.
 *
 * Sections and rows both keep the order the items arrive in, which the server
 * has already resolved from lineIndex (falling back to creation order for
 * invoices raised before batching).
 *
 * `editable` decides whether a row pins itself to the amount on record:
 *   - View (default) keeps `storedAmount`, so a line priced before the current
 *     fields existed still shows the real charge instead of a re-derived $0.
 *   - Edit must NOT carry it. rowSubtotal() prefers storedAmount whenever it is
 *     present, so an editable row that kept it would show the old figure no
 *     matter what the user typed — the totals would never move.
 */
export const groupsFromItems = (items = [], { editable = false } = {}) => {
  const map = new Map();
  // A line's own files are whatever it holds beyond the shared invoice files.
  // They are shown on the row as saved (read-only) chips.
  const invoiceFiles = new Set(sharedAttachmentKeys(items));
  items.forEach((item) => {
    const ref = (Array.isArray(item?.properties) ? item.properties : [])[0] || item?.property || {};
    const propertyId = ref.propertyId ? String(ref.propertyId) : "";
    const buildingName = ref.buildingName || "";
    // Every line carries its own rate type; a section is purely property + building.
    const rateType = resolveRateType(item);
    const key = `${propertyId}::${buildingName}`;
    if (!map.has(key)) {
      // `title` is resolved server-side by enrichInvoices, so the View can name
      // the property even when the viewer's own property list does not contain
      // it — an auditor's list is empty, and a Select would render blank.
      map.set(key, {
        id: uid(),
        propertyId,
        buildingName,
        propertyLabel: ref.title || item?.propertyTitle || "",
        rows: [],
      });
    }
    const row = {
      id: uid(),
      // The line this row was built from. Edit mode sends it back as the line's
      // `_id` so the server updates that document; the read-only View never
      // uses it.
      itemId: item?._id,
      rateType,
      unitNo: ref.unitNo || "",
      jobDescription: ref.jobDescription || "",
      hours: item?.hours ?? "",
      minutes: item?.minutes ?? "",
      amountPerHour: item?.amountPerHour ?? "",
      flatAmount: rateType === "flat" ? flatAmountOf(item) : "",
      materialCost: item?.materialCost ?? "",
      gstRate: item?.gstRate ?? DEFAULT_GST_RATE,
      comment: item?.comment || "",
      files: (item?.attachments || [])
        .filter((key) => !invoiceFiles.has(key))
        .map((key) => ({ id: key, key, name: key.split("/").pop(), signedUrl: null, saved: true })),
    };
    if (!editable) {
      // What the invoice actually records, so a flat-priced line is not
      // re-derived to zero from absent hours and rate.
      row.storedAmount = Number.isFinite(Number(item?.amount)) ? Number(item.amount) : undefined;
    }
    map.get(key).rows.push(row);
  });
  return Array.from(map.values());
};

/**
 * The `lines` array for PUT /invoice/group/:groupId/lines, straight from the
 * form sections. A row that came from the server carries `itemId` and is sent
 * with `_id` so the server updates that document; a row added in the form has
 * none and is created. Only the live pricing field set is sent, mirroring the
 * create payload, so a flat line can never carry a stale hourly rate.
 */
export const linesPayloadFromGroups = (groups = []) =>
  groups.flatMap((group) =>
    (group.rows || []).map((row) => {
      const rateType = row.rateType || "hourly";
      return {
        ...(row.itemId ? { _id: row.itemId } : {}),
        propertyId: group.propertyId,
        buildingName: (group.buildingName || "").trim(),
        unitNo: (row.unitNo || "").trim(),
        jobDescription: (row.jobDescription || "").trim(),
        rateType,
        ...(rateType === "flat"
          ? { flatAmount: Number(row.flatAmount) }
          : {
              hours: Number(row.hours) || 0,
              minutes: Number(row.minutes) || 0,
              amountPerHour: Number(row.amountPerHour),
            }),
        materialCost: Number(row.materialCost) || 0,
        gstRate: Number(row.gstRate),
        comment: (row.comment || "").trim(),
      };
    })
  );
